import type React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { keys } from 'lodash';
import moment from 'moment-timezone';
import { TIMEZONE } from '../../utils/constants';
import classNames from 'classnames';
import { SegmentedControl } from '@digital/react-aviva-atom';

import { TimeCard } from './time-card/TimeCard';

import './TimePicker.css';

export interface Props {
    onSelect: (time: Date) => void;
    selectedDate: Date;
    times: endpoints.LessonTime[];
    disabled?: boolean;
}

export enum TimeOfDay {
    AM = 1,
    PM = 2,
}

const TimePicker: React.FC<Props> = ({ onSelect, selectedDate, times, disabled = false }) => {
    const [timeOfDay, setTimeOfDay] = useState<TimeOfDay | undefined>(undefined);
    const [hour, setHour] = useState<number>(-1);
    const [minute, setMinute] = useState<number>(-1);
    const [availableTimes, setAvailableTimes] = useState<Date[]>([]);
    const [searchTimes, setSearchTimes] = useState<Date[]>([]);
    const [selectedTime, setSelectedTime] = useState<Date | undefined>(undefined);

    // Initialize available times when the component mounts or when `times` changes
    useEffect(() => {
        setAvailableTimes(
            keys(times).map((key) => moment(key).tz(TIMEZONE).toDate())
        );
    }, [times]);

    // Reset state when `selectedDate` changes
    useEffect(() => {
        setTimeOfDay(undefined);
        setHour(-1);
        setMinute(-1);
        setAvailableTimes(
            keys(times).map((key) => moment(key).tz(TIMEZONE).toDate())
        );
        setSearchTimes([]);
    }, [selectedDate, times]);

    useEffect(() => {
        // if (minute !== -1 && hour !== -1 && timeOfDay) {
        search();
        // }
    }, [minute, hour, timeOfDay]); // Trigger search() whenever minute changes

    const search = useCallback(() => {
        if (minute !== -1 && hour !== -1 && timeOfDay) {
            let selectedMoment = moment(selectedDate).tz(TIMEZONE);

            selectedMoment.hours(hour % 12);
            if (timeOfDay === TimeOfDay.PM) {
                selectedMoment.hours((hour % 12) + 12);
            }
            selectedMoment.minute(minute);

            let matchedTimes: Date[] = [];
            availableTimes.some((time) => {
                const timeMoment = moment(time).tz(TIMEZONE);
                if (timeMoment.isSame(selectedMoment, 'minute')) {
                    matchedTimes = [time];
                    return true;
                }
                if (timeMoment.isBefore(selectedMoment, 'minute')) {
                    matchedTimes[0] = time;
                } else if (timeMoment.isAfter(selectedMoment, 'minute')) {
                    matchedTimes.push(time);
                    return true;
                }
                return false;
            });

            setSearchTimes(matchedTimes);
        }
    }, [minute, hour, timeOfDay, selectedDate, availableTimes]);

    const handleTimeOfDayChange = (value: string | number | string[] | undefined) => {
        setSelectedTime(undefined);
        if (typeof value === 'number') {
            setTimeOfDay(value as TimeOfDay);
        }
    };

    const handleHourChange = (e: React.FormEvent<HTMLSelectElement>) => {
        setSelectedTime(undefined);
        setHour(parseInt(e.currentTarget.value, 10));
    };

    const handleMinuteChange = (e: React.FormEvent<HTMLSelectElement>) => {
        setSelectedTime(undefined);
        setMinute(parseInt(e.currentTarget.value, 10));
    };

    const handleTimeClick = (time: Date) => {
        setSelectedTime(time);
        onSelect(time);
    };

    const timeOfDayOptions = [
        { title: 'AM', value: TimeOfDay.AM },
        { title: 'PM', value: TimeOfDay.PM },
    ];

    const hours = Array.from({ length: 12 }, (_, i) => ({
        label: `${i + 1}`,
        value: i + 1,
    }));

    const minutes = [
        { label: '00', value: 0 },
        { label: '15', value: 15 },
        { label: '30', value: 30 },
        { label: '45', value: 45 },
    ];

    return (
        <div>
            <div className="time-container" data-testid="time-container">
                <div className="time-row">
                    <span className="a-dropdown">
                        <select
                            title="Select hour"
                            data-testid="hour-dropdown"
                            className="a-dropdown__select"
                            value={hour}
                            onChange={handleHourChange}
                            disabled={disabled}
                        >
                            <option value={-1} disabled>
                                HH
                            </option>
                            {hours.map((hour) => (
                                <option value={hour.value} key={hour.value}>
                                    {hour.label}
                                </option>
                            ))}
                        </select>
                        <span className="a-dropdown__ui" />
                    </span>
                </div>
                <div className="time-row">
                    <span className="a-dropdown">
                        <select
                            title="Select minute"
                            data-testid="minute-dropdown"
                            className="a-dropdown__select"
                            value={minute}
                            onChange={handleMinuteChange}
                            disabled={disabled}
                        >
                            <option value={-1} disabled>
                                MM
                            </option>
                            {minutes.map((minute) => (
                                <option value={minute.value} key={minute.value}>
                                    {minute.label}
                                </option>
                            ))}
                        </select>
                        <span className="a-dropdown__ui" />
                    </span>
                </div>
                <div className="time-row m-form-row__content">
                    <SegmentedControl
                        data-testid="time-of-day-toggle"
                        options={timeOfDayOptions}
                        value={timeOfDay}
                        onChange={handleTimeOfDayChange}
                    />
                </div>
            </div>
            {searchTimes.length > 0 && (
                <div>
                    <h3 className="text__center time-info">
                        <strong>
                            {'The following time' +
                                (searchTimes.length > 1 ? 's are' : ' is') +
                                ' available'}
                        </strong>
                    </h3>
                    <div className="possible-times" data-testid="possible-times">
                        {searchTimes.map((time) => (
                            <div
                                className={classNames('time', {
                                    selected: time === selectedTime,
                                })}
                                onClick={() => handleTimeClick(time)}
                                key={time.toISOString()}
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleTimeClick(time);  
                                    }
                                }}
                            >
                                <TimeCard date={time} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimePicker;